import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Clear,
  Save,
  Cancel
} from '@mui/icons-material';

const Annees = () => {
  const [annees, setAnnees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    intitule: '',
    dateDebut: '',
    dateFin: '',
    estActive: false
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/annees', { headers: getAuthHeaders() });
      setAnnees(res.data || []);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const filteredData = annees.filter(item => {
    const search = searchTerm.toLowerCase();
    return (item.intitule || '').toLowerCase().includes(search);
  });

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    if (item) {
      setSelectedItem(item);
      setFormData({
        intitule: item.intitule || '',
        dateDebut: item.dateDebut?.split('T')[0] || '',
        dateFin: item.dateFin?.split('T')[0] || '',
        estActive: item.estActive || false
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      intitule: '',
      dateDebut: '',
      dateFin: '',
      estActive: false
    });
  };

  const handleSubmit = async () => {
    try {
      if (modalMode === 'add') {
        await axios.post('/api/annees', formData, { headers: getAuthHeaders() });
        setSuccessMessage('Année ajoutée avec succès');
      } else {
        await axios.put(`/api/annees/${selectedItem._id}`, formData, { headers: getAuthHeaders() });
        setSuccessMessage('Année modifiée avec succès');
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'opération');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer définitivement ${item.intitule} ?`)) return;
    try {
      await axios.delete(`/api/annees/${item._id}`, { headers: getAuthHeaders() });
      setSuccessMessage('Année supprimée avec succès');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.put(`/api/annees/${item._id}`, { estActive: !item.estActive }, { headers: getAuthHeaders() });
      setSuccessMessage(`Année ${!item.estActive ? 'activée' : 'désactivée'} avec succès`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'opération');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            📅 Gestion des Années Universitaires
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} année(s) trouvée(s)
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal('add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Nouvelle Année
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par intitulé..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Intitulé</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Date Début</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Date Fin</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
              <TableRow key={item._id} hover>
                <TableCell sx={{ fontWeight: 500 }}>{item.intitule}</TableCell>
                <TableCell>{item.dateDebut ? new Date(item.dateDebut).toLocaleDateString('fr-FR') : '-'}</TableCell>
                <TableCell>{item.dateFin ? new Date(item.dateFin).toLocaleDateString('fr-FR') : '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={item.estActive ? '✅ Active' : '❌ Inactive'}
                    size="small"
                    sx={{ bgcolor: item.estActive ? '#dcfce7' : '#fee2e2', color: item.estActive ? '#166534' : '#991b1b' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="Activer/Désactiver">
                      <IconButton size="small" color={item.estActive ? 'success' : 'default'} onClick={() => handleToggleActive(item)}>
                        <Switch size="small" checked={item.estActive} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifier">
                      <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', item)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucune année trouvée</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? '➕ Ajouter une année' : '✏️ Modifier l\'année'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Intitulé *"
              value={formData.intitule}
              onChange={(e) => setFormData({ ...formData, intitule: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Date Début"
              type="date"
              value={formData.dateDebut}
              onChange={(e) => setFormData({ ...formData, dateDebut: e.target.value })}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Date Fin"
              type="date"
              value={formData.dateFin}
              onChange={(e) => setFormData({ ...formData, dateFin: e.target.value })}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.estActive}
                  onChange={(e) => setFormData({ ...formData, estActive: e.target.checked })}
                />
              }
              label="Année Active"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} startIcon={<Cancel />}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<Save />} sx={{ bgcolor: '#667eea' }}>
            {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Annees;


